import React, { ReactElement, ReactNode, useCallback, useState } from "react";

import styled from "styled-components";

import {
  BORDER_STYLE_OPTIONS,
  CANVAS_ELEMENTS,
  COLOR_OPTIONS,
  CanvasElementDef,
  ELEMENT_BODIES,
  FONT_SIZE_OPTIONS,
  OPACITY_OPTIONS,
  RADIUS_OPTIONS,
  SHAPE_TYPE_ICON,
  ShapeElementState,
  ShapeType,
  StickyTextStyle,
  THICKNESS_OPTIONS,
  TextAlign,
  ALIGN_OPTIONS,
  STYLE_OPTIONS,
  TextStyleKey,
} from "./elements";
import { FileElementDef, formatFileSize } from "./fileElement";
import { LinkElementDef } from "./linkElement";
import {
  OUTLINE_MAX_WIDTH,
  OUTLINE_MIN_WIDTH,
  OUTLINE_WIDTH,
  StyledChooseRow,
  StyledPanelChooseSlot,
} from "./outline";
import { ProjectElementDef, projectElementTitle } from "./projectElements";
import { Checkbox } from "../../components/Checkbox";
import { ChooseV2 } from "../../components/ChooseV2";
import { Dot } from "../../components/Dot";
import { EditableText } from "../../components/EditableText";
import { IconButton } from "../../components/IconButton";
import {
  AttachmentIcon,
  CircleSlashIcon,
  InsertLinkIcon,
  LockIcon,
  NoteIcon,
  ProjectIcon,
  TaskIcon,
} from "../../components/Icons";
import { Input, InputAdornment, InputNumber } from "../../components/Input";
import { Label } from "../../components/Label";
import { Select } from "../../components/Select";
import { SelectTrigger } from "../../components/SelectTrigger";
import { Tooltip } from "../../components/Tooltip";
import { Body2, Caption1, Header3 } from "../../components/Typography";

/* ------------------------------------------------------------------ */
/* Right Inspector — selected-element properties panel                 */
/* ------------------------------------------------------------------ */

/* Which right-docked panel is open. Only "inspector" is built here; "comments"
   is reserved for the next iteration. */
export type RightPanel = "inspector" | "comments" | null;

/* ── Inspector capability traits ──────────────────────────────────────────
   The Inspector is TRAIT-DRIVEN: a selected element declares which property
   groups it supports, and only those groups render. This is the "interface
   with implementation traits" model — the interface is the full set of groups,
   and each element kind overrides the flags it does not support (a sticky has
   no stroke, and its height is auto because the note grows with its content).
   New element kinds slot in by returning their own trait set. */
export interface ElementTraits {
  geometry: boolean;
  fill: boolean;
  /* Whether the element's name is the board's to change. Almost everything on a
     canvas is named by whoever put it there — but a project or task card is a
     VIEW of a record that lives in the project, and renaming the view would
     either lie about the record or silently rename it from a whiteboard. So the
     title is shown and never edited. Mirrors the app's read-only entity title. */
  renamable?: boolean;
  /* Whether this element's fill may be Transparent. Distinct from `fill`: a
     sticky HAS a fill group but must not be transparent (its colour is its
     defining property), whereas an outline-only shape is valid. Mirrors the
     app's `fillTransparent` trait. */
  fillTransparent?: boolean;
  stroke: boolean;
  text: boolean;
  autoWidth?: boolean;
  autoHeight?: boolean;
  /* The viewer may look at this element's properties and change none of them.
     One flag, because for a limited-access card it is one fact: every control
     the panel would otherwise offer is withheld together with the entity. */
  readOnly?: boolean;
}

export const elementTraits = (def: CanvasElementDef): ElementTraits =>
  def.kind === "sticky"
    ? {
        geometry: true,
        fill: true,
        fillTransparent: false,
        stroke: false,
        text: true,
        autoHeight: true,
      }
    : {
        geometry: true,
        fill: true,
        fillTransparent: true,
        stroke: true,
        text: true,
      };

/**
 * A project / task card's traits. It is the most constrained element on the
 * board, and every "false" here is a fact about the card rather than a feature
 * that has not been built yet:
 *
 *   fill / stroke / text   the card's appearance is the design system's, not
 *                          the board's — recolouring it would make a task look
 *                          like something it is not on another person's screen.
 *   autoHeight             the card is content-sized; only its width is given.
 *   renamable              the title belongs to the task / project record.
 *
 * Rotation is absent for the same reason it is absent on link and file cards:
 * the Geometry group for a card simply does not render one.
 */
export const PROJECT_ELEMENT_TRAITS: ElementTraits = {
  geometry: true,
  fill: false,
  stroke: false,
  text: false,
  autoHeight: true,
  renamable: false,
};

/**
 * A LIMITED-ACCESS card's traits: the project card's, with everything the
 * viewer could have changed taken away.
 *
 * The panel is not empty, and that is deliberate — geometry still renders, so
 * the viewer can see that the thing has a position and a size, which is
 * precisely the amount that Workload's limited cell exposes (its data shape is
 * a quantity and an offset). It simply cannot be edited.
 */
export const LIMITED_ELEMENT_TRAITS: ElementTraits = {
  ...PROJECT_ELEMENT_TRAITS,
  readOnly: true,
};

export const TYPE_LABEL: Record<ShapeType, string> = {
  rectangle: "Rectangle",
  roundedRectangle: "Rounded Rectangle",
  circle: "Circle",
  triangle: "Triangle",
};

export const sameColor = (a: string | undefined, b: string): boolean =>
  (a ?? "").toLowerCase() === b.toLowerCase();

/* Right dock — mirrors StyledOutlineSidebar but docked on the RIGHT: border on
   the left edge, resizer on the left edge. Same 260–780 width band. */
export const StyledInspectorSidebar = styled.aside`
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--selectable);
  background-color: var(--page-paper-main);
  font-family: -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue",
    Arial, sans-serif;
`;

/* Grab strip on the LEFT edge, because the panel is docked right — dragging it
   left widens. Same token pair as the outline resizer: selectable grey at
   rest, the hover token while grabbed. */
export const StyledInspectorResizer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: -2px;
  width: 5px;
  cursor: col-resize;
  z-index: 2;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 2px;
    width: 1px;
    background-color: var(--selectable-active);
    opacity: 0;
    transition: opacity 150ms ease;
  }

  &:hover::after,
  &.resizing::after {
    opacity: 1;
  }
`;

export const StyledInspectorBody = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

/* Wraps the property groups. The Properties/Comments switch above does the
   separating, so the first group drops its top hairline — no divider directly
   under the header, matching the left sidebar. Inner groups keep separators. */
export const StyledPropGroups = styled.div`
  display: flex;
  flex-direction: column;

  > div:first-child {
    border-top: none;
  }
`;

export const StyledInspectorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 0 16px;
  box-sizing: border-box;
  flex-shrink: 0;

  /* Icon in a fixed, centred box (like the outline's type icon and the sidebar
     header) so it lines up with the live EditableText's text rather than the
     taller input box. */
  .inspector-type-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: var(--color-theme-700);
  }

  .inspector-type-icon svg {
    width: 20px;
    height: 20px;
  }

  .inspector-name {
    flex: 1;
    min-width: 0;
  }

  /* A title that cannot be renamed is plain Typography rather than a live
     EditableText input, so it needs its own truncation — and the same 4px of
     side padding EditableText's input carries, or read-only titles would sit
     4px to the left of editable ones. */
  .inspector-name-static {
    padding: 0 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

/* Empty state — same muted treatment the outline uses for unnamed rows. */
export const StyledInspectorEmpty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: var(--color-theme-600);
`;

/* Non-collapsible section: bold title, no chevron / drag handle, separated from
   the section above by a hairline. */
export const StyledPropGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid var(--border-primary);

  .group-title {
    color: var(--color-theme-800);
  }
`;

export const StyledPropRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StyledFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

/* InputNumber's wrapper is a fixed 360px and SelectTrigger a fixed 300px — both
   overflow the narrow dock, so force every inspector control to fill its cell. */
export const StyledControl = styled.div`
  width: 100%;

  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  .c-input-wrapper {
    width: 100%;
  }

  .wb-inspector-trigger {
    width: 100%;
  }

  .wb-color-value {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* A transparent fill has no colour to show, so it renders the design-system
     CircleSlashIcon (a ring with a 45° slash) instead of an invisible dot. */
  .wb-color-value .wb-transparent-swatch {
    flex: none;
    color: var(--color-theme-500);
  }
`;

/* Locked checkbox row — the DS Checkbox renders only the box, so it is paired
   with a Label the way the design system expects. */
export const StyledCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* The element body is HTML (the API's `body` field). The Inspector renders it
   formatted and read-only for now — editing comes later. Raw text needs an
   explicit font-family (no global reset), and the child tags get light prose
   styling. */
export const StyledBody = styled.div`
  width: 100%;
  /* Collapsed to a reasonable height; a long body scrolls inside the field. */
  max-height: 140px;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue",
    Arial, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-theme-800);

  p {
    margin: 0 0 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 18px;
  }

  li {
    margin: 2px 0;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  a {
    color: var(--color-primary);
  }
`;

/* Read-only "Auto" field for an auto-sized dimension (a sticky's height). */
export const StyledAutoField = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  box-sizing: border-box;
  border: 1px solid var(--color-theme-400);
  border-radius: var(--ac-br-8);
  background-color: var(--color-theme-200);
  color: var(--color-theme-600);
  font-size: 13px;
`;

export const StyledSegmented = styled.div`
  display: flex;
  gap: 4px;

  /* Icon segments (text alignment): size the glyph and center it in the cell so
     each Choose reads as an equal-width icon button. */
  svg {
    display: block;
    width: 16px;
    height: 16px;
  }
`;

export interface NumberFieldProps {
  label: string;
  value: number;
  min?: number;
  /* Unit shown as a right adornment inside the field (px, °), the same way
     InputCurrency shows the currency code. */
  unit?: string;
  onCommit: (value: number) => void;
}

export const NumberField = ({
  label,
  value,
  min,
  unit,
  onCommit,
}: NumberFieldProps): ReactElement => (
  <StyledPropRow>
    <Label size="small" weight="regular">
      {label}
    </Label>
    <StyledControl>
      <InputNumber
        className="wb-inspector-num"
        value={value}
        decimalLength={0}
        min={min}
        update
        endAdornment={
          unit ? (
            <InputAdornment disablePointerEvents>
              <Caption1 color="secondary">{unit}</Caption1>
            </InputAdornment>
          ) : undefined
        }
        onChange={(next) => {
          if (next !== undefined && !Number.isNaN(next)) {
            onCommit(Math.round(next));
          }
        }}
      />
    </StyledControl>
  </StyledPropRow>
);

export const AutoField = ({ label }: { label: string }): ReactElement => (
  <StyledPropRow>
    <Label size="small" weight="regular">
      {label}
    </Label>
    <StyledAutoField>Auto</StyledAutoField>
  </StyledPropRow>
);

/* A geometry value the viewer may READ but not change — the same field
   `AutoField` and the file card's Size already use, with a real value in it.
   Showing the number greyed out is the point: a limited card still has a
   position and a size, and those are the only facts about it there are. */
export const ReadOnlyField = ({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactElement => (
  <StyledPropRow>
    <Label size="small" weight="regular">
      {label}
    </Label>
    <StyledAutoField>{value}</StyledAutoField>
  </StyledPropRow>
);

export interface OptionSelectProps {
  options: { id: string | number; name: string }[];
  value: string | number;
  triggerLabel: string;
  onChange: (value: string | number) => void;
}

export const OptionSelect = ({
  options,
  value,
  triggerLabel,
  onChange,
}: OptionSelectProps): ReactElement => (
  <StyledControl>
    <Select
      type="single"
      disableSearch
      forceCloseMenu
      options={options}
      selected={value}
      onChange={(next) => onChange(Array.isArray(next) ? next[0] : next)}
      target={
        <SelectTrigger className="wb-inspector-trigger">
          {triggerLabel}
        </SelectTrigger>
      }
    />
  </StyledControl>
);

export const TRANSPARENT_OPTION = {
  id: "transparent",
  name: "Transparent",
  color: "transparent",
  textColor: "transparent",
};

/* The name shown on the color picker's trigger, resolved from the current hex. */
export const colorName = (hex: string | undefined): string => {
  if (!hex || sameColor(hex, "transparent")) {
    return "Transparent";
  }

  const match = COLOR_OPTIONS.find((option) => sameColor(hex, option.color));
  return match ? match.name : "Custom";
};

export interface ColorPickerFieldProps {
  value: string | undefined;
  disabled?: boolean;
  /* Adds "Transparent" as the first option, rendered as the DS CircleSlashIcon.
     Driven by the element's `fillTransparent` trait, so only SHAPE fill opts in
     — sticky fill (its colour is the point) and stroke colour never do. */
  includeTransparent?: boolean;
  onSelect: (color: string) => void;
}

/* The regular design-system named-colour picker (the one in the label dialogs):
   a Select whose trigger is a Dot + colour name, and whose menu is the standard
   dot-and-radio list. Custom colours are deferred. */
export const ColorPickerField = ({
  value,
  disabled,
  includeTransparent,
  onSelect,
}: ColorPickerFieldProps): ReactElement => {
  const options = includeTransparent
    ? [TRANSPARENT_OPTION, ...COLOR_OPTIONS]
    : COLOR_OPTIONS;
  const isTransparent = !value || sameColor(value, "transparent");

  return (
    <StyledControl className={disabled ? "disabled" : undefined}>
      <Select
        type="single"
        disableSearch
        forceCloseMenu
        options={options}
        selected={isTransparent ? "transparent" : value}
        onChange={(next) =>
          onSelect(String(Array.isArray(next) ? next[0] : next))
        }
        target={
          <SelectTrigger className="wb-inspector-trigger">
            <span className="wb-color-value">
              {isTransparent ? (
                <CircleSlashIcon
                  width={14}
                  height={14}
                  fill="currentColor"
                  className="wb-transparent-swatch"
                />
              ) : (
                <Dot color={value} size={14} />
              )}
              {colorName(value)}
            </span>
          </SelectTrigger>
        }
      />
    </StyledControl>
  );
};

export interface RightInspectorProps {
  selectedId: string | null;
  positions: Record<string, { x: number; y: number }>;
  setPositions: React.Dispatch<
    React.SetStateAction<Record<string, { x: number; y: number }>>
  >;
  sizes: Record<string, { width: number; height: number }>;
  setSizes: React.Dispatch<
    React.SetStateAction<Record<string, { width: number; height: number }>>
  >;
  rotations: Record<string, number>;
  setRotations: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  shapes: Record<string, ShapeElementState>;
  setShapes: React.Dispatch<
    React.SetStateAction<Record<string, ShapeElementState>>
  >;
  stickyStyle: StickyTextStyle;
  setStickyStyle: React.Dispatch<React.SetStateAction<StickyTextStyle>>;
  links: LinkElementDef[];
  setLinks: React.Dispatch<React.SetStateAction<LinkElementDef[]>>;
  files: FileElementDef[];
  setFiles: React.Dispatch<React.SetStateAction<FileElementDef[]>>;
  createdShapes: CanvasElementDef[];
  projectElements: ProjectElementDef[];
  setProjectElements: React.Dispatch<React.SetStateAction<ProjectElementDef[]>>;
  locks: Record<string, boolean>;
  setLocks: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export const RightInspector = ({
  selectedId,
  positions,
  setPositions,
  sizes,
  setSizes,
  rotations,
  setRotations,
  shapes,
  setShapes,
  stickyStyle,
  setStickyStyle,
  links,
  setLinks,
  files,
  setFiles,
  createdShapes,
  projectElements,
  setProjectElements,
  locks,
  setLocks,
}: RightInspectorProps): ReactElement => {
  const [width, setWidth] = useState(OUTLINE_WIDTH);
  const [resizing, setResizing] = useState(false);
  /* Mock only — element display names are not persisted anywhere; they default
     to the type label until renamed here. */
  const [names, setNames] = useState<Record<string, string>>({});
  /* Which inspector tab is showing: the property groups we design, or the
     upcoming comments stream (details TBD). */
  const [tab, setTab] = useState<"properties" | "comments">("properties");

  /* Docked right, so the grab strip is on the LEFT edge and dragging left
     widens: the delta is subtracted rather than added (see LeftSidebar). */
  const startResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = event.currentTarget.parentElement?.offsetWidth ?? 0;
    const previousUserSelect = document.body.style.userSelect;

    setResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const handleMove = (moveEvent: MouseEvent): void => {
      const next = startWidth - (moveEvent.clientX - startX);
      setWidth(Math.min(OUTLINE_MAX_WIDTH, Math.max(OUTLINE_MIN_WIDTH, next)));
    };

    const handleUp = (): void => {
      setResizing(false);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }, []);

  const def = selectedId
    ? [...CANVAS_ELEMENTS, ...createdShapes].find(
        (element) => element.id === selectedId
      )
    : undefined;
  const linkDef = selectedId
    ? links.find((link) => link.id === selectedId)
    : undefined;
  const fileDef = selectedId
    ? files.find((file) => file.id === selectedId)
    : undefined;
  const projectDef = selectedId
    ? projectElements.find((entry) => entry.id === selectedId)
    : undefined;

  const shell = (content: ReactNode): ReactElement => (
    <StyledInspectorSidebar style={{ width }}>
      <StyledInspectorResizer
        className={resizing ? "resizing" : ""}
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize inspector"
      />
      {content}
    </StyledInspectorSidebar>
  );

  /* Description — inline, multi-line editable text, shown first for every
     element kind. */
  /* Read-only formatted body (HTML from the API's `body` field). No label; when
     the body is empty the field is not rendered at all. No edit yet. */
  const renderBody = (id: string): ReactNode => {
    const html = ELEMENT_BODIES[id] ?? "";
    if (!html) {
      return null;
    }

    // eslint-disable-next-line react/no-danger
    return <StyledBody dangerouslySetInnerHTML={{ __html: html }} />;
  };

  /* The Locked checkbox — shown for every element kind, toggling a design lock
     flag keyed by element id. */
  /* `forced` is the limited-access case: the element is locked FOR THIS VIEWER
     and the checkbox is the honest place to say so — ticked, and disabled so it
     reads as a statement about the element rather than a switch that was left
     on. */
  const renderLocked = (id: string, forced = false): ReactElement => (
    <StyledCheckboxRow>
      <Checkbox
        id={`wb-lock-${id}`}
        checked={forced || (locks[id] ?? false)}
        disabled={forced}
        onChange={() =>
          setLocks((current) => ({ ...current, [id]: !(current[id] ?? false) }))
        }
      />
      <Label htmlFor={`wb-lock-${id}`} size="small" weight="regular">
        Locked
      </Label>
    </StyledCheckboxRow>
  );

  /* Geometry for a card (link / file / project / task): X/Y from the shared
     positions map, an editable width and an auto height (cards are
     content-sized). Cards cannot be rotated, so there is no Rotation field.
     `readOnly` keeps the same three values in the same three places and swaps
     the inputs for read-only fields — a limited card's geometry is information,
     not a control. */
  const renderCardGeometry = (
    id: string,
    cardWidth: number,
    onWidth: (width: number) => void,
    readOnly = false
  ): ReactElement => {
    const cardPosition = positions[id] ?? { x: 0, y: 0 };

    return (
      <StyledPropGroup>
        <Body2 weight="bold" className="group-title">
          Geometry
        </Body2>
        <StyledFieldGrid>
          {readOnly ? (
            <ReadOnlyField label="X" value={`${cardPosition.x} px`} />
          ) : (
            <NumberField
              label="X"
              unit="px"
              value={cardPosition.x}
              onCommit={(next) =>
                setPositions((current) => ({
                  ...current,
                  [id]: { ...(current[id] ?? { x: 0, y: 0 }), x: next },
                }))
              }
            />
          )}
          {readOnly ? (
            <ReadOnlyField label="Y" value={`${cardPosition.y} px`} />
          ) : (
            <NumberField
              label="Y"
              unit="px"
              value={cardPosition.y}
              onCommit={(next) =>
                setPositions((current) => ({
                  ...current,
                  [id]: { ...(current[id] ?? { x: 0, y: 0 }), y: next },
                }))
              }
            />
          )}
        </StyledFieldGrid>
        <StyledFieldGrid>
          {readOnly ? (
            <ReadOnlyField label="Width" value={`${cardWidth} px`} />
          ) : (
            <NumberField
              label="Width"
              unit="px"
              value={cardWidth}
              min={1}
              onCommit={onWidth}
            />
          )}
          <AutoField label="Height" />
        </StyledFieldGrid>
      </StyledPropGroup>
    );
  };

  /* Header + panel switch + body, shared by every element kind — only the
     groups inside differ. `renamable` false swaps the live EditableText for
     read-only text, for elements whose name is not the board's to change. */
  const renderInspectorBody = (
    icon: ReactNode,
    headerName: string,
    id: string,
    groups: ReactNode,
    renamable = true
  ): ReactElement => (
    <StyledInspectorBody>
      <StyledInspectorHeader>
        <span className="inspector-type-icon">{icon}</span>
        <span className="inspector-name">
          {renamable ? (
            <EditableText
              value={headerName}
              variant="Header 3"
              weight="bold"
              onSave={(event) =>
                setNames((current) => ({
                  ...current,
                  [id]: event.target.value,
                }))
              }
              inputProps={{ maxLength: 120 }}
            />
          ) : (
            <Header3 className="inspector-name-static" title={headerName}>
              {headerName}
            </Header3>
          )}
        </span>
      </StyledInspectorHeader>

      <StyledChooseRow>
        <StyledPanelChooseSlot>
          <ChooseV2
            required
            options={[
              { id: "properties", name: "Properties" },
              { id: "comments", name: "Comments" },
            ]}
            selected={[tab]}
            onChange={(selected) =>
              setTab(selected[0] as "properties" | "comments")
            }
          />
        </StyledPanelChooseSlot>
      </StyledChooseRow>

      {tab === "comments" ? (
        <StyledInspectorEmpty>
          <Body2>The comments stream is coming soon.</Body2>
        </StyledInspectorEmpty>
      ) : (
        <StyledPropGroups>{groups}</StyledPropGroups>
      )}
    </StyledInspectorBody>
  );

  /* Nothing selected. */
  if (!def && !linkDef && !fileDef && !projectDef) {
    return shell(
      <StyledInspectorEmpty>
        <Body2>Select an element to see its properties.</Body2>
      </StyledInspectorEmpty>
    );
  }

  /* Project / task card — the most constrained element here. Its look is not
     the board's to change (no Fill, Stroke or Text), and its title belongs to
     the record it shows, so the header is read-only. What is left is what the
     board really does own: where the card sits, how wide it is, and whether it
     is locked. */
  if (projectDef) {
    const limited = projectDef.limited === true;
    const cardTraits = limited
      ? LIMITED_ELEMENT_TRAITS
      : PROJECT_ELEMENT_TRAITS;

    return shell(
      renderInspectorBody(
        /* A limited card gets a NEUTRAL icon. The task and project glyphs would
           each say which kind of record is behind the card, and the kind is
           part of what the viewer is not entitled to. */
        limited ? (
          <LockIcon />
        ) : projectDef.kind === "project" ? (
          <ProjectIcon />
        ) : (
          <TaskIcon />
        ),
        projectElementTitle(projectDef),
        projectDef.id,
        <>
          <StyledPropGroup>
            {renderLocked(projectDef.id, cardTraits.readOnly)}
          </StyledPropGroup>
          {cardTraits.geometry
            ? renderCardGeometry(
                projectDef.id,
                projectDef.width,
                (width) =>
                  setProjectElements((current) =>
                    current.map((entry) =>
                      entry.id === projectDef.id ? { ...entry, width } : entry
                    )
                  ),
                cardTraits.readOnly
              )
            : null}
        </>,
        cardTraits.renamable
      )
    );
  }

  /* Link card — its URL plus the shared Locked flag. */
  if (linkDef) {
    return shell(
      renderInspectorBody(
        <InsertLinkIcon />,
        names[linkDef.id] ?? (linkDef.url || "Link"),
        linkDef.id,
        <>
          <StyledPropGroup>
            {renderBody(linkDef.id)}
            <StyledPropRow>
              <Label size="small" weight="regular">
                URL
              </Label>
              <StyledControl>
                <Input
                  value={linkDef.url ?? ""}
                  placeholder="https://…"
                  onChange={(event) =>
                    setLinks((current) =>
                      current.map((link) =>
                        link.id === linkDef.id
                          ? { ...link, url: event.target.value }
                          : link
                      )
                    )
                  }
                />
              </StyledControl>
            </StyledPropRow>
            {renderLocked(linkDef.id)}
          </StyledPropGroup>
          {renderCardGeometry(linkDef.id, linkDef.width, (width) =>
            setLinks((current) =>
              current.map((link) =>
                link.id === linkDef.id ? { ...link, width } : link
              )
            )
          )}
        </>
      )
    );
  }

  /* File card — read-only size plus the shared Locked flag. */
  if (fileDef) {
    return shell(
      renderInspectorBody(
        <AttachmentIcon />,
        names[fileDef.id] ??
          (fileDef.file
            ? `${fileDef.file.base}.${fileDef.file.extension}`
            : "File"),
        fileDef.id,
        <>
          <StyledPropGroup>
            {renderBody(fileDef.id)}
            <StyledPropRow>
              <Label size="small" weight="regular">
                Size
              </Label>
              <StyledAutoField>
                {fileDef.file ? formatFileSize(fileDef.file.size) : "—"}
              </StyledAutoField>
            </StyledPropRow>
            {renderLocked(fileDef.id)}
          </StyledPropGroup>
          {renderCardGeometry(fileDef.id, fileDef.width, (width) =>
            setFiles((current) =>
              current.map((file) =>
                file.id === fileDef.id ? { ...file, width } : file
              )
            )
          )}
        </>
      )
    );
  }

  /* Shape / sticky from here on. */
  if (!def) {
    return shell(
      <StyledInspectorEmpty>
        <Body2>Select an element to see its properties.</Body2>
      </StyledInspectorEmpty>
    );
  }

  const traits = elementTraits(def);
  const shape = def.kind === "shape" ? shapes[def.id] : null;
  const size = sizes[def.id] ?? { width: def.width, height: def.height };
  const position = positions[def.id] ?? { x: def.x, y: def.y };
  const rotation = rotations[def.id] ?? 0;

  const typeLabel = shape ? TYPE_LABEL[shape.type] : "Sticky note";
  const name = names[def.id] ?? typeLabel;

  const updateShape = (partial: Partial<ShapeElementState>): void =>
    setShapes((current) => ({
      ...current,
      [def.id]: { ...current[def.id], ...partial },
    }));

  /* Fill is unified across kinds: a shape writes shape.fill, a sticky its note
     colour. A transparent colour means no fill. */
  const currentFill = shape ? shape.fill : stickyStyle.color;
  const currentFillOpacity = shape
    ? shape.fillOpacity
    : stickyStyle.fillOpacity;

  const setFill = (color: string): void => {
    if (shape) {
      updateShape({ fill: color });
    } else {
      setStickyStyle({ ...stickyStyle, color });
    }
  };

  const setFillOpacity = (next: number): void => {
    if (shape) {
      updateShape({ fillOpacity: next });
    } else {
      setStickyStyle({ ...stickyStyle, fillOpacity: next });
    }
  };

  const currentFontSize = shape ? shape.fontSize : stickyStyle.fontSize;
  const currentAlign: TextAlign = shape
    ? shape.align ?? "left"
    : stickyStyle.align;

  const setFontSize = (next: number): void => {
    if (shape) {
      updateShape({ fontSize: next });
    } else {
      setStickyStyle({ ...stickyStyle, fontSize: next });
    }
  };

  const setAlign = (next: TextAlign): void => {
    if (shape) {
      updateShape({ align: next });
    } else {
      setStickyStyle({ ...stickyStyle, align: next });
    }
  };

  const styleFlags = shape ?? stickyStyle;

  const toggleStyle = (key: TextStyleKey): void => {
    if (shape) {
      updateShape({ [key]: !shape[key] });
    } else {
      setStickyStyle({ ...stickyStyle, [key]: !stickyStyle[key] });
    }
  };

  return shell(
    renderInspectorBody(
      shape ? SHAPE_TYPE_ICON[shape.type] : <NoteIcon />,
      name,
      def.id,
      <>
        {/* Element-specific properties — no header, sits above Geometry. */}
        <StyledPropGroup>
          {renderBody(def.id)}
          {shape ? (
            <StyledPropRow>
              <Label size="small" weight="regular">
                Shape
              </Label>
              <OptionSelect
                options={(
                  [
                    "rectangle",
                    "roundedRectangle",
                    "circle",
                    "triangle",
                  ] as ShapeType[]
                ).map((shapeType) => ({
                  id: shapeType,
                  name: TYPE_LABEL[shapeType],
                }))}
                value={shape.type}
                triggerLabel={TYPE_LABEL[shape.type]}
                onChange={(next) => updateShape({ type: next as ShapeType })}
              />
            </StyledPropRow>
          ) : null}
          {renderLocked(def.id)}
        </StyledPropGroup>

        {traits.geometry ? (
          <StyledPropGroup>
            <Body2 weight="bold" className="group-title">
              Geometry
            </Body2>
            <StyledFieldGrid>
              <NumberField
                label="X"
                unit="px"
                value={position.x}
                onCommit={(next) =>
                  setPositions((current) => ({
                    ...current,
                    [def.id]: { ...current[def.id], x: next },
                  }))
                }
              />
              <NumberField
                label="Y"
                unit="px"
                value={position.y}
                onCommit={(next) =>
                  setPositions((current) => ({
                    ...current,
                    [def.id]: { ...current[def.id], y: next },
                  }))
                }
              />
            </StyledFieldGrid>
            <StyledFieldGrid>
              {traits.autoWidth ? (
                <AutoField label="Width" />
              ) : (
                <NumberField
                  label="Width"
                  unit="px"
                  value={size.width}
                  min={1}
                  onCommit={(next) =>
                    setSizes((current) => ({
                      ...current,
                      [def.id]: {
                        width: next,
                        height: current[def.id]?.height ?? def.height,
                      },
                    }))
                  }
                />
              )}
              {traits.autoHeight ? (
                <AutoField label="Height" />
              ) : (
                <NumberField
                  label="Height"
                  unit="px"
                  value={size.height}
                  min={1}
                  onCommit={(next) =>
                    setSizes((current) => ({
                      ...current,
                      [def.id]: {
                        width: current[def.id]?.width ?? def.width,
                        height: next,
                      },
                    }))
                  }
                />
              )}
            </StyledFieldGrid>
            <StyledFieldGrid>
              <NumberField
                label="Rotation"
                unit="°"
                value={rotation}
                onCommit={(next) =>
                  setRotations((current) => ({ ...current, [def.id]: next }))
                }
              />
            </StyledFieldGrid>
          </StyledPropGroup>
        ) : null}

        {traits.fill ? (
          <StyledPropGroup>
            <Body2 weight="bold" className="group-title">
              Fill
            </Body2>
            <StyledFieldGrid>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Color
                </Label>
                <ColorPickerField
                  includeTransparent={traits.fillTransparent}
                  value={currentFill}
                  onSelect={setFill}
                />
              </StyledPropRow>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Opacity
                </Label>
                <OptionSelect
                  options={OPACITY_OPTIONS.map((opacity) => ({
                    id: opacity,
                    name: `${opacity}%`,
                  }))}
                  value={currentFillOpacity}
                  triggerLabel={`${currentFillOpacity}%`}
                  onChange={(next) => setFillOpacity(Number(next))}
                />
              </StyledPropRow>
            </StyledFieldGrid>
          </StyledPropGroup>
        ) : null}

        {traits.stroke && shape ? (
          <StyledPropGroup>
            <Body2 weight="bold" className="group-title">
              Stroke
            </Body2>
            <StyledPropRow>
              <Label size="small" weight="regular">
                Pattern
              </Label>
              <StyledSegmented>
                <ChooseV2
                  required
                  options={BORDER_STYLE_OPTIONS.map((option) => ({
                    id: option.label,
                    name: option.label,
                  }))}
                  selected={[
                    BORDER_STYLE_OPTIONS.find(
                      (option) => option.value === shape.strokeDash
                    )?.label ?? "Solid",
                  ]}
                  onChange={(selected) =>
                    updateShape({
                      strokeDash: BORDER_STYLE_OPTIONS.find(
                        (option) => option.label === selected[0]
                      )?.value,
                    })
                  }
                />
              </StyledSegmented>
            </StyledPropRow>
            <StyledFieldGrid>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Thickness
                </Label>
                <OptionSelect
                  options={THICKNESS_OPTIONS.map((thickness) => ({
                    id: thickness,
                    name: `${thickness} pt`,
                  }))}
                  value={shape.strokeWidth}
                  triggerLabel={`${shape.strokeWidth} pt`}
                  onChange={(next) =>
                    updateShape({ strokeWidth: Number(next) })
                  }
                />
              </StyledPropRow>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Color
                </Label>
                <ColorPickerField
                  value={shape.stroke}
                  onSelect={(color) => updateShape({ stroke: color })}
                />
              </StyledPropRow>
            </StyledFieldGrid>
            <StyledFieldGrid>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Radius
                </Label>
                <OptionSelect
                  options={RADIUS_OPTIONS.map((radius) => ({
                    id: radius,
                    name: `${radius} px`,
                  }))}
                  value={shape.borderRadius}
                  triggerLabel={`${shape.borderRadius} px`}
                  onChange={(next) =>
                    updateShape({ borderRadius: Number(next) })
                  }
                />
              </StyledPropRow>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Opacity
                </Label>
                <OptionSelect
                  options={OPACITY_OPTIONS.map((opacity) => ({
                    id: opacity,
                    name: `${opacity}%`,
                  }))}
                  value={shape.strokeOpacity}
                  triggerLabel={`${shape.strokeOpacity}%`}
                  onChange={(next) =>
                    updateShape({ strokeOpacity: Number(next) })
                  }
                />
              </StyledPropRow>
            </StyledFieldGrid>
          </StyledPropGroup>
        ) : null}

        {traits.text ? (
          <StyledPropGroup>
            <Body2 weight="bold" className="group-title">
              Text
            </Body2>
            <StyledFieldGrid>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Style
                </Label>
                <StyledSegmented>
                  {STYLE_OPTIONS.map((option) => (
                    <Tooltip key={option.key} title={option.label}>
                      <IconButton
                        variant="text gray"
                        active={styleFlags[option.key]}
                        aria-label={option.label}
                        onClick={() => toggleStyle(option.key)}
                      >
                        {option.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </StyledSegmented>
              </StyledPropRow>
              <StyledPropRow>
                <Label size="small" weight="regular">
                  Size
                </Label>
                <OptionSelect
                  options={FONT_SIZE_OPTIONS.map((fontSize) => ({
                    id: fontSize,
                    name: `${fontSize} pt`,
                  }))}
                  value={currentFontSize}
                  triggerLabel={`${currentFontSize} pt`}
                  onChange={(next) => setFontSize(Number(next))}
                />
              </StyledPropRow>
            </StyledFieldGrid>
            <StyledPropRow>
              <Label size="small" weight="regular">
                Alignment
              </Label>
              <StyledSegmented>
                {ALIGN_OPTIONS.map((option) => (
                  <Tooltip key={option.value} title={option.label}>
                    <IconButton
                      variant="text gray"
                      active={currentAlign === option.value}
                      aria-label={option.label}
                      onClick={() => setAlign(option.value)}
                    >
                      {option.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </StyledSegmented>
            </StyledPropRow>
          </StyledPropGroup>
        ) : null}
      </>
    )
  );
};
